/**
 * SessionView.tsx
 *
 * Purpose: Main game session view for Mythbound Adventure Studio™.
 * Created: 2025-01-19
 * Modified: 2026-02-23
 *
 * WHO: Players and GMs during active game sessions.
 * WHAT: Displays the main game interface with chat, characters, and controls.
 * WHERE: Mythbound.App/src/pages/SessionView.tsx
 * WHY: Central interface for real-time tabletop gameplay.
 *
 * ISSUE #021 FIX: Wired GameControl buttons with appropriate handlers
 *
 * Updated 2026-02-23: Section 3 - Session View & Chat Wiring (Issues #019, #020)
 *   - Replaced ALL mock character data with API-fetched characters
 *   - Replaced mock chat messages with WebSocket real-time feed
 *   - Session state loaded from API + WebSocket subscription
 *   - Chat messages styled per design spec (player/NPC/system/voice)
 *   - Pulse-glow CSS animation for active speaker indicator
 *   - Chat input sends via WebSocket with optimistic update
 *
 * INTEGRATION POINTS:
 * - SessionService: For real session data via API polling
 * - WebSocket: For real-time chat, voice events, and session updates
 * - CharacterService: For real character roster data
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Grid,
  GridItem,
  VStack,
  HStack,
  Avatar,
  IconButton,
  Badge,
  Divider,
  useDisclosure,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Skeleton,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { MinusIcon, SmallAddIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import { audioProcessor } from '../integration/AudioProcessor';
import { SessionControls } from '../components/session/SessionControls';
import { DiceRoller } from '../components/dice/DiceRoller';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { characterService } from '../services/api/CharacterService';
import { mapCharacterToRosterEntry, type SessionRosterEntry } from './sessionRosterAdapter';
import { buildApiUrl } from '../utils/environment';
import { useAuth } from '../context/AuthContext';
import { useCampaignContext } from '../context/CampaignContext';
import { useOptionalCampaignRealtime } from '../context/CampaignRealtimeContext';
// PRD001A R5 (2026-09-22): SessionView previously polled
// `sessionService.getSessionState` on its own 3-second interval, entirely
// independent of `SessionContext`'s own identical poll — a second,
// independent authority for the same resource (the release review's
// "SessionView maintains separate local state" finding). `useSession()`
// now reads the single shared poll `SessionRouteProvider`/`SessionProvider`
// already seed and maintain for this route (see App.tsx/SessionContext.tsx).
import { useSession } from '../context/SessionContext';
import { webSocketService } from '../services/websocket/websocketService';
// Updated 2026-02-23: VoicePanel floating overlay for unified voice UI (Section 5)
import { VoicePanel } from '../components/voice';

// =============================================================================
// ISSUE #021: Import new components for wired game controls
// =============================================================================
import { DiceRollerModal } from '../components/session/DiceRollerModal';
import { SpellbookDrawer } from '../components/session/SpellbookDrawer';
import { ComingSoonModal } from '../components/common/ComingSoonModal';
import { CampaignHistory } from '../components/session/CampaignHistory';

/**
 * Session View Component
 * 
 * Implements the Session View page template as specified in the UI/UX requirements.
 * Features:
 * - Main interaction area with The MM™ interface
 * - Character sheets in side panel
 * - Game controls styled as magical implements
 */

// =============================================================================
// SESSION CHARACTER CARD COMPONENT
// Updated 2026-02-23: Added isSpeaking prop for pulse-glow animation
// =============================================================================

interface SessionCharacterProps {
  name: string;
  race: string;
  /** Formatted display text ("Hidden"/"Not set"/real value) — never a
   * fabricated "Fighter". See `sessionRosterAdapter.ts`. */
  classText: string;
  /** Formatted display text — never a fabricated "1". */
  levelText: string;
  avatar: string;
  /** Formatted display text for current HP — real value (incl. "0"),
   * "Hidden", or "Not set". */
  hpText: string;
  /** Formatted display text for max HP — same three states as `hpText`. */
  maxHpText: string;
  /** Percentage in [0, 100], or `null` when hp/max_hp aren't BOTH known
   * real values with a positive max — PRD001A R2/R6 (2026-09-23) forbids
   * fabricating a bar fraction in that case; render no bar / a neutral one. */
  healthRatio: number | null;
  isActive?: boolean;
  isSpeaking?: boolean; // Updated 2026-02-23: speaking indicator with pulse-glow
  onClick?: () => void;
}

/**
 * Pulse-glow CSS animation for active speaker indicator.
 * Applied to character cards in participants list during active speech.
 * Uses Mythic Blue (#3BE8FF) as specified in the design.
 * The @keyframes are injected via Chakra's sx prop on the character card.
 *
 * CSS animation reference:
 * @keyframes pulse-glow {
 *   0% { box-shadow: 0 0 5px #3BE8FF; }
 *   50% { box-shadow: 0 0 15px #3BE8FF; }
 *   100% { box-shadow: 0 0 5px #3BE8FF; }
 * }
 */

const SessionCharacterCard: React.FC<SessionCharacterProps> = ({
  name,
  race,
  classText,
  levelText,
  avatar,
  hpText,
  maxHpText,
  healthRatio,
  isActive = false,
  isSpeaking = false,
  onClick,
}) => {
  // healthRatio is null whenever hp/max_hp aren't BOTH known real values
  // with a positive max (see sessionRosterAdapter.ts) — no bar/color is
  // fabricated in that case, never a silent 0%.
  const hasKnownHealth = healthRatio !== null;
  let healthColor = mythboundTheme.colors.functional.success;

  if (hasKnownHealth) {
    if (healthRatio < 30) {
      healthColor = mythboundTheme.colors.functional.error;
    } else if (healthRatio < 70) {
      healthColor = mythboundTheme.colors.functional.warning;
    }
  }

  return (
    <Box
      p={3}
      borderRadius="md"
      bg={isActive ? 'rgba(59, 232, 255, 0.1)' : 'rgba(17, 17, 22, 0.6)'}
      borderWidth="1px"
      borderColor={isActive ? mythboundTheme.colors.primary.main : mythboundTheme.colors.accent.brown}
      boxShadow={isActive ? mythboundTheme.shadows.glow : 'none'}
      transition="all 0.3s"
      _hover={{
        bg: 'rgba(59, 232, 255, 0.05)',
        borderColor: mythboundTheme.colors.primary.main,
      }}
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      /* Updated 2026-02-23: Pulsing Mythic Blue glow during active speech */
      animation={isSpeaking ? 'pulse-glow 2s ease-in-out infinite' : 'none'}
      sx={isSpeaking ? {
        '@keyframes pulse-glow': {
          '0%': { boxShadow: '0 0 5px #3BE8FF' },
          '50%': { boxShadow: '0 0 15px #3BE8FF' },
          '100%': { boxShadow: '0 0 5px #3BE8FF' },
        },
      } : {}}
    >
      <HStack spacing={3} align="center">
        <Avatar
          name={name}
          src={avatar}
          size="md"
          bg={mythboundTheme.colors.secondary.purple}
        />
        <VStack align="start" spacing={0} flex="1">
          <Text fontWeight="bold" fontSize="md">{name}</Text>
          <Text fontSize="xs" color={mythboundTheme.colors.accent.silver}>
            {race} {classText}, Level {levelText}
          </Text>
          <Flex w="100%" mt={1} align="center">
            <Box
              w="100%"
              h="6px"
              bg="rgba(255, 82, 82, 0.2)"
              borderRadius="full"
              overflow="hidden"
            >
              {/* No fill at all when health isn't known — an empty/neutral
                  track is honest; a fabricated 0% fill would look like
                  "confirmed dead," which is not what "unknown" means. */}
              {hasKnownHealth && (
                <Box
                  h="100%"
                  w={`${healthRatio}%`}
                  bg={healthColor}
                  borderRadius="full"
                />
              )}
            </Box>
            <Text fontSize="xs" ml={2} whiteSpace="nowrap">
              {hpText}/{maxHpText}
            </Text>
          </Flex>
        </VStack>
      </HStack>
    </Box>
  );
};

// Game Control Button Component
interface GameControlProps {
  icon: string;
  label: string;
  onClick?: () => void;
  isActive?: boolean;
}

const GameControl: React.FC<GameControlProps> = ({ icon, label, onClick, isActive = false }) => {
  return (
    <VStack
      spacing={1}
      align="center"
      onClick={onClick}
      cursor="pointer"
      transition="all 0.3s"
      opacity={isActive ? 1 : 0.7}
      _hover={{
        opacity: 1,
        transform: 'translateY(-2px)',
      }}
    >
      <Box
        fontSize="xl"
        bg={isActive ? 'rgba(59, 232, 255, 0.2)' : 'rgba(59, 232, 255, 0.05)'}
        color={mythboundTheme.colors.primary.main}
        borderRadius="full"
        p={3}
        boxShadow={isActive ? mythboundTheme.shadows.glow : 'none'}
      >
        {icon}
      </Box>
      <Text fontSize="xs" color={mythboundTheme.colors.secondary.white}>
        {label}
      </Text>
    </VStack>
  );
};

// =============================================================================
// CHAT MESSAGE TYPES AND COMPONENT
// Updated 2026-02-23: Styled per design spec (player/NPC/system/voice events)
// =============================================================================

/**
 * Message type determines visual styling:
 * - 'player': Character name (bold, Cinzel) + text (Montserrat) + timestamp
 * - 'npc': Gold left border (4px), prefixed with NPC name
 * - 'system': Silver left border (4px), italic text
 * - 'voice': Inline system message, silver border (voice join/leave)
 * - 'gm': GM messages styled with gold theme
 */
type ChatMessageType = 'player' | 'npc' | 'system' | 'voice' | 'gm';

interface ChatMessageProps {
  sender: string;
  content: string;
  timestamp: string;
  messageType: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  sender,
  content,
  timestamp,
  messageType,
}) => {
  // Compute visual styling based on message type per design spec
  let bgColor = 'rgba(17, 17, 22, 0.6)';
  let borderLeftWidth = '0px';
  let borderLeftColor = 'transparent';
  let borderWidth = '1px';
  let borderColor = mythboundTheme.colors.accent.brown;
  let nameColor = mythboundTheme.colors.secondary.white;
  let nameFontFamily = mythboundTheme.typography.fontFamily.heading; // Cinzel for character names
  let textFontFamily = mythboundTheme.typography.fontFamily.body; // Montserrat for body text
  let isItalic = false;
  let showSender = true;

  switch (messageType) {
    case 'gm':
      // GM / NPC dialogue from The Myth Master™: gold left border
      bgColor = 'rgba(217, 181, 85, 0.1)';
      borderLeftWidth = '4px';
      borderLeftColor = mythboundTheme.colors.primary.gold; // Gold #D9B555
      nameColor = mythboundTheme.colors.primary.gold;
      break;
    case 'npc':
      // NPC dialogue: gold left border (4px), prefixed with NPC name
      bgColor = 'rgba(217, 181, 85, 0.06)';
      borderLeftWidth = '4px';
      borderLeftColor = mythboundTheme.colors.primary.gold; // Gold #D9B555
      nameColor = mythboundTheme.colors.primary.gold;
      break;
    case 'system':
      // System events: silver left border (4px), italic text
      bgColor = 'rgba(197, 197, 199, 0.06)';
      borderLeftWidth = '4px';
      borderLeftColor = mythboundTheme.colors.accent.silver; // Silver #C5C5C7
      nameColor = mythboundTheme.colors.accent.silver;
      isItalic = true;
      break;
    case 'voice':
      // Voice join/leave: inline system message, silver border
      bgColor = 'rgba(197, 197, 199, 0.04)';
      borderLeftWidth = '4px';
      borderLeftColor = mythboundTheme.colors.accent.silver; // Silver #C5C5C7
      nameColor = mythboundTheme.colors.accent.silver;
      isItalic = true;
      showSender = false; // Voice events display content inline
      break;
    case 'player':
    default:
      // Player messages: standard styling with bold Cinzel name
      bgColor = 'rgba(17, 17, 22, 0.6)';
      borderColor = mythboundTheme.colors.accent.brown;
      break;
  }

  return (
    <Box
      p={3}
      borderRadius="md"
      bg={bgColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderLeftWidth={borderLeftWidth}
      borderLeftColor={borderLeftColor}
      mb={3}
    >
      {showSender ? (
        <>
          <Flex justify="space-between" align="center" mb={1}>
            <Text
              fontWeight="bold"
              color={nameColor}
              fontFamily={nameFontFamily}
            >
              {sender}
              {messageType === 'gm' && (
                <Badge ml={2} colorScheme="yellow" fontSize="xs">
                  GM
                </Badge>
              )}
            </Text>
            {/* Timestamp: muted silver, right-aligned per design spec */}
            <Text
              fontSize="xs"
              color={mythboundTheme.colors.accent.silver}
              fontFamily={textFontFamily}
            >
              {timestamp}
            </Text>
          </Flex>
          <Text
            fontSize="sm"
            fontFamily={textFontFamily}
            fontStyle={isItalic ? 'italic' : 'normal'}
          >
            {content}
          </Text>
        </>
      ) : (
        /* Voice events: single-line inline with timestamp */
        <Flex justify="space-between" align="center">
          <Text
            fontSize="sm"
            color={mythboundTheme.colors.accent.silver}
            fontStyle="italic"
            fontFamily={textFontFamily}
          >
            {content}
          </Text>
          <Text fontSize="xs" color={mythboundTheme.colors.accent.silver}>
            {timestamp}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

// =============================================================================
// MAIN SESSION VIEW COMPONENT
// =============================================================================

export const SessionView: React.FC = () => {
  const navigate = useNavigate();
  const [activeCharacter, setActiveCharacter] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showSessionControlsPanel, setShowSessionControlsPanel] = useState(true);
  const [showDashboardDicePanel, setShowDashboardDicePanel] = useState(true);
  const [showEncounterDicePanel, setShowEncounterDicePanel] = useState(true);
  const [showSidebarDicePanel, setShowSidebarDicePanel] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [bellActive, setBellActive] = useState(false);
  const [isCampaignHistoryOpen, setCampaignHistoryOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // DISCLOSURE HOOKS FOR MODALS/DRAWERS
  // Issue #021: Added for wired game control buttons
  // ---------------------------------------------------------------------------

  /** Character drawer (mobile view) - ALREADY EXISTS */
  const { isOpen, onOpen, onClose } = useDisclosure();

  /** Dice Roller Modal - Opens focused dice rolling interface */
  const {
    isOpen: isDiceModalOpen,
    onOpen: onDiceModalOpen,
    onClose: onDiceModalClose
  } = useDisclosure();

  /** Spellbook Drawer - Opens character spell reference */
  const {
    isOpen: isSpellbookOpen,
    onOpen: onSpellbookOpen,
    onClose: onSpellbookClose
  } = useDisclosure();

  /** Map Modal - Shows "Coming Soon" message */
  const {
    isOpen: isMapModalOpen,
    onOpen: onMapModalOpen,
    onClose: onMapModalClose
  } = useDisclosure();

  // ---------------------------------------------------------------------------
  // SESSION DATA - sourced from URL params, auth context, and API
  // ---------------------------------------------------------------------------
  const { id: routeSessionId } = useParams<{ id: string }>();
  const { userInfo } = useAuth();
  const toast = useToast();

  const sessionId = routeSessionId ?? '';
  const participantId = userInfo?.id ?? '';
  const playerScreenName = userInfo?.screenName || userInfo?.username || 'Adventurer';
  const isGM = userInfo?.role === 'gm' || userInfo?.role === 'admin';

  // ---------------------------------------------------------------------------
  // CAMPAIGN CONTEXT -- needed for fetching characters by campaignId
  // Updated 2026-02-23: replaces hardcoded campaignId="" in CharacterCreationModal
  // ---------------------------------------------------------------------------
  const { currentCampaign } = useCampaignContext();
  const campaignRealtime = useOptionalCampaignRealtime();
  const campaignSnapshot = campaignRealtime?.snapshot;


  // Warn in development if critical IDs are missing (replaces silent demo fallbacks)
  useEffect(() => {
    if (!routeSessionId && process.env.NODE_ENV === 'development') {
      console.warn('[SessionView] No session ID in route params. Session features will not function.');
    }
    if (!userInfo?.id && process.env.NODE_ENV === 'development') {
      console.warn('[SessionView] No authenticated user ID. Participant features will not function.');
    }
  }, [routeSessionId, userInfo?.id]);

  // Session state — from the shared SessionContext poll (PRD001A R5), not a
  // second independent fetch. `SessionRouteProvider` (App.tsx) already seeds
  // this from the URL's session identity before SessionView ever mounts.
  // (The previous local `sessionLoading` flag was set but never actually
  // read anywhere in this file — not reintroduced here.)
  const { sessionState, setMasterMode } = useSession();
  // A direct session route may coexist with a global selection from a previous
  // campaign. Only a matching authorized projection may label this session.
  const sessionCampaignName = sessionState?.campaign_id
    ? (campaignSnapshot?.campaign.campaign_id === sessionState.campaign_id
      ? campaignSnapshot.campaign.campaign_name
      : currentCampaign?.campaign_id === sessionState.campaign_id ? currentCampaign.campaign_name : null)
    : null;
  const historyCampaignId = sessionState?.session_id === sessionId ? sessionState.campaign_id : null;
  const historySnapshot = campaignSnapshot?.campaign.campaign_id === historyCampaignId ? campaignSnapshot : null;

  // ---------------------------------------------------------------------------
  // CHARACTER ROSTER -- fetched from API, replaces mock data
  // Updated 2026-02-23: Characters loaded via CharacterService.listCharactersByCampaign()
  // ---------------------------------------------------------------------------
  const [characters, setCharacters] = useState<SessionRosterEntry[]>([]);
  const [charactersLoading, setCharactersLoading] = useState(true);
  const [charactersError, setCharactersError] = useState<string | null>(null);
  const [rosterRetry, setRosterRetry] = useState(0);
  const rosterRequestGeneration = useRef(0);
  // Track which participants are currently speaking (by participant ID)
  const [speakingParticipants, setSpeakingParticipants] = useState<Set<string>>(new Set());

  // Chat state -- input is driven by contentEditable ref, not React state
  const [isSending, setIsSending] = useState(false);
  const chatInputRef = useRef<HTMLDivElement>(null);

  // Active character tracking -- uses API-fetched characters array
  const characterName = characters[activeCharacter]?.name || 'Character';
  const characterId = characters[activeCharacter]?.character_id || '';

  // ---------------------------------------------------------------------------
  // GAME CONTROL HANDLERS
  // Issue #021 Fix: Wire up all GameControl button click handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle "Roll Dice" game control.
   * Opens the DiceRollerModal for focused dice rolling.
   */
  const handleRollDice = useCallback(() => {
    onDiceModalOpen();
  }, [onDiceModalOpen]);

  // Saved notes are campaign history records, not chat messages or a free-form notes route.
  const handleNotes = useCallback(() => {
    setCampaignHistoryOpen((open) => !open);
  }, []);

  /**
   * Handle "Map" game control.
   * Opens "Coming Soon" modal since Map feature is not yet implemented.
   *
   * INTEGRATION POINT: When Map feature is implemented:
   * 1. Create /components/session/MapModal.tsx or /pages/GameMap.tsx
   * 2. Replace onMapModalOpen with actual map functionality
   */
  const handleMap = useCallback(() => {
    onMapModalOpen();
  }, [onMapModalOpen]);

  /**
   * Handle "Spellbook" game control.
   * Opens the SpellbookDrawer for spell reference.
   */
  const handleSpellbook = useCallback(() => {
    onSpellbookOpen();
  }, [onSpellbookOpen]);

  /**
   * Handle "Settings" game control.
   * Navigates to settings page (shows 404/Coming Soon until implemented).
   *
   * INTEGRATION POINT: When Settings feature is implemented:
   * 1. Create /pages/Settings.tsx
   * 2. Add route to App.tsx
   */
  const handleSettings = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  // Added 2026-03-11: Navigate to session forum page
  const handleForum = useCallback(() => {
    navigate(`/session/${sessionId}/forum`);
  }, [navigate, sessionId]);

  const handleVoiceToggle = useCallback(async () => {
    if (isCapturing) {
      console.log('[SessionView] Stopping audio capture...');
      const stopped = audioProcessor.stopCapture();
      if (stopped) {
        setIsCapturing(false);
        console.log('[SessionView] Audio capture stopped.');
      } else {
        console.error('[SessionView] Failed to stop audio capture.');
        // Optionally provide user feedback (e.g., toast notification)
      }
    } else {
      console.log('[SessionView] Starting audio capture...');
      const started = await audioProcessor.startCapture();
      if (started) {
        setIsCapturing(true);
        console.log('[SessionView] Audio capture started.');
      } else {
        console.error('[SessionView] Failed to start audio capture.');
        // Optionally provide user feedback
      }
    }
  }, [isCapturing]); // Dependency: isCapturing state

  useEffect(() => {
    // Return a cleanup function
    return () => {
      console.log('[SessionView] Unmounting. Disposing AudioProcessor...');
      audioProcessor.dispose();
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  useEffect(() => {
    let isMounted = true;

    const fetchUnread = async () => {
      try {
        const response = await fetch(buildApiUrl('/events/notifications/unread-count'));
        if (!response.ok) {
          throw new Error(`Unread count request failed: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted && typeof data.unread_count === 'number') {
          setUnreadNotifications(data.unread_count);
        }
      } catch (error) {
        if (isMounted) {
          console.debug('[SessionView] Unread count fetch skipped', error);
        }
      }
    };

    fetchUnread();
    const intervalId = window.setInterval(fetchUnread, 60_000);
    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const handleBellClick = useCallback(() => {
    setBellActive(prev => !prev);
  }, []);

  // ---------------------------------------------------------------------------
  // FETCH CHARACTER ROSTER FROM API
  // Uses the persisted session's campaign identity; the optional realtime
  // snapshot is only a refresh hint, never a source of Character records.
  // ---------------------------------------------------------------------------
  // Keep refresh dependencies explicit: lifecycle polling must not refetch
  // the roster unless campaign identity or an accepted snapshot changes.
  const sessionCampaignId = sessionState?.campaign_id;
  const hasSessionState = Boolean(sessionState);
  const rosterSnapshot = campaignSnapshot?.campaign.campaign_id === sessionCampaignId ? campaignSnapshot : null;
  useEffect(() => {
    const campaignId = sessionCampaignId;
    if (!campaignId) {
      setCharacters([]);
      setCharactersError(hasSessionState ? 'This session has no campaign roster available.' : null);
      setCharactersLoading(!hasSessionState);
      return;
    }

    const requestId = ++rosterRequestGeneration.current;
    const controller = new AbortController();
    setCharactersLoading(true);
    setCharactersError(null);
    // Keep failures visibly distinct from a successful zero-character response.
    setCharacters([]);

    const fetchCharacters = async () => {
      try {
        const roster = await characterService.listCharactersByCampaign(campaignId, { signal: controller.signal });
        if (requestId !== rosterRequestGeneration.current) return;
        // Preserve hidden / unknown / real values, including zero, in the
        // viewer-specific projection used by session cards. Missing ownership
        // metadata cannot confer owner rendering to the first roster entry.
        setCharacters(roster.map(c => mapCharacterToRosterEntry(c, c.is_owner === true)));
      } catch (error) {
        if (requestId !== rosterRequestGeneration.current || controller.signal.aborted) return;
        setCharacters([]);
        setCharactersError(error instanceof Error ? error.message : 'Failed to load character roster');
        console.warn('[SessionView] Failed to load character roster:', error);
      } finally {
        if (requestId === rosterRequestGeneration.current) setCharactersLoading(false);
      }
    };

    void fetchCharacters();
    return () => {
      controller.abort();
      // The generation fence also covers a transport or test double that does
      // not honor AbortSignal, and keeps reconnect snapshots latest-wins.
      if (requestId === rosterRequestGeneration.current) rosterRequestGeneration.current += 1;
    };
  }, [sessionCampaignId, hasSessionState, rosterSnapshot, rosterRetry]);

  // ---------------------------------------------------------------------------
  // VOICE ACTIVITY TRACKING
  // Updated 2026-02-23: Listen for voice_activity events to drive speaking indicator
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleVoiceActivity = (data: any) => {
      if (data?.participantId && typeof data.isSpeaking === 'boolean') {
        setSpeakingParticipants(prev => {
          const next = new Set(prev);
          if (data.isSpeaking) {
            next.add(data.participantId);
          } else {
            next.delete(data.participantId);
          }
          return next;
        });
      }
    };

    webSocketService.on('voice_activity', handleVoiceActivity);
    return () => { webSocketService.off('voice_activity', handleVoiceActivity); };
  }, []);

  // ---------------------------------------------------------------------------
  // WEBSOCKET CHAT MESSAGE LISTENERS
  // Updated 2026-02-23: Maps incoming messages to typed ChatMessageType
  // Handles: chat.message, chat.system, chat.npc, voice.join, voice.leave
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Player and GM chat messages
    const handleChatMessage = (data: any) => {
      if (data?.sender && data?.content) {
        const msgType: ChatMessageType = data.is_gm ? 'gm' : 'player';
        setChatMessages(prev => [...prev, {
          sender: data.sender,
          content: data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          messageType: msgType,
        }]);
      }
    };

    // System events (dice rolls, status changes, etc.)
    const handleSystemMessage = (data: any) => {
      if (data?.content) {
        setChatMessages(prev => [...prev, {
          sender: data.sender || 'System',
          content: data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          messageType: 'system' as ChatMessageType,
        }]);
      }
    };

    // NPC dialogue messages
    const handleNpcMessage = (data: any) => {
      if (data?.sender && data?.content) {
        setChatMessages(prev => [...prev, {
          sender: data.sender,
          content: data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          messageType: 'npc' as ChatMessageType,
        }]);
      }
    };

    // Voice join/leave events -- displayed as inline system messages
    const handleVoiceJoin = (data: any) => {
      const charName = data?.characterName || data?.participantId || 'Someone';
      setChatMessages(prev => [...prev, {
        sender: '',
        content: `[${charName} joined voice]`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        messageType: 'voice' as ChatMessageType,
      }]);
    };

    const handleVoiceLeave = (data: any) => {
      const charName = data?.characterName || data?.participantId || 'Someone';
      setChatMessages(prev => [...prev, {
        sender: '',
        content: `[${charName} left voice]`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        messageType: 'voice' as ChatMessageType,
      }]);
    };

    webSocketService.on('chat.message', handleChatMessage);
    webSocketService.on('chat.system', handleSystemMessage);
    webSocketService.on('chat.npc', handleNpcMessage);
    webSocketService.on('voice_join', handleVoiceJoin);
    webSocketService.on('voice_leave', handleVoiceLeave);

    return () => {
      webSocketService.off('chat.message', handleChatMessage);
      webSocketService.off('chat.system', handleSystemMessage);
      webSocketService.off('chat.npc', handleNpcMessage);
      webSocketService.off('voice_join', handleVoiceJoin);
      webSocketService.off('voice_leave', handleVoiceLeave);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // SEND CHAT MESSAGE VIA WEBSOCKET
  // Updated 2026-02-23: Uses typed message system, optimistic update,
  // error toast on failure (5s, top-right per spec)
  // ---------------------------------------------------------------------------
  const handleSendMessage = useCallback(() => {
    const text = chatInputRef.current?.innerText?.trim() || '';
    if (!text) return;

    if (!sessionId) {
      toast({
        title: 'No Active Session',
        description: 'Cannot send messages without an active session.',
        status: 'warning',
        duration: 3000,
        position: 'top-right',
        isClosable: true,
      });
      return;
    }

    setIsSending(true);

    const sent = webSocketService.send('chat.message', {
      session_id: sessionId,
      content: text,
      sender: playerScreenName,
      character_name: characterName,
    });

    if (sent) {
      // Optimistic update: show message immediately in the local feed
      setChatMessages(prev => [...prev, {
        sender: playerScreenName,
        content: text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        messageType: (isGM ? 'gm' : 'player') as ChatMessageType,
      }]);
      if (chatInputRef.current) {
        chatInputRef.current.innerText = '';
      }
    } else {
      toast({
        title: 'Message Failed',
        description: 'Could not send message. Check your connection.',
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
    }

    setIsSending(false);
  }, [sessionId, playerScreenName, characterName, isGM, toast]);

  // Handle Enter key in chat input
  const handleChatKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // ---------------------------------------------------------------------------
  // CHAT MESSAGES STATE -- starts empty, populated by WebSocket real-time feed
  // Updated 2026-02-23: Removed ALL mock chat messages (Issue #019)
  // Messages arrive via WebSocket listeners registered above
  // ---------------------------------------------------------------------------
  const [chatMessages, setChatMessages] = useState<ChatMessageProps[]>([]);

  return (
    <>
    <Grid
      h="calc(100vh - 80px)"
      templateRows="1fr auto"
      templateColumns={{ base: "1fr", lg: "1fr 300px" }}
      gap={4}
    >
      {/* Main Game Area */}
      <GridItem rowSpan={1} colSpan={1} position="relative">
        <Box
          h="100%"
          bg="rgba(17, 17, 22, 0.7)"
          borderRadius="md"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.brown}
          p={4}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {showSessionControlsPanel ? (
            <Box
              mb={4}
              p={3}
              borderRadius="md"
              bg="rgba(0, 0, 0, 0.4)"
              border="1px solid rgba(255, 255, 255, 0.1)"
            >
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" fontSize="sm" color={mythboundTheme.colors.primary.gold}>
                  Session Controls
                </Text>
                <IconButton
                  aria-label="Hide session controls"
                  size="sm"
                  icon={<MinusIcon />}
                  onClick={() => setShowSessionControlsPanel(false)}
                />
              </Flex>
              <SessionControls
                sessionId={sessionId}
                isGM={isGM}
                participantId={participantId}
                onSessionStarted={(newSessionId) => navigate(`/session/${encodeURIComponent(newSessionId)}`)}
                compact
              />
            </Box>
          ) : (
            <Button
              size="sm"
              mb={4}
              leftIcon={<SmallAddIcon />}
              onClick={() => setShowSessionControlsPanel(true)}
            >
              Show Session Controls
            </Button>
          )}

          <Flex
            align="center"
            justify="space-between"
            mb={4}
            flexWrap="wrap"
            gap={2}
          >
            {/* Updated 2026-02-23: Session title from API state or campaign name */}
            <Heading
              size="md"
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.primary.gold}
            >
              {sessionCampaignName || 'Active Session'}
              {sessionState?.round_number != null && (
                <Text as="span" fontSize="sm" color={mythboundTheme.colors.accent.silver} ml={2}>
                  Round {sessionState.round_number}
                </Text>
              )}
            </Heading>
            <NotificationBell
              unreadCount={unreadNotifications}
              onClick={handleBellClick}
              isActive={bellActive}
            />
          </Flex>
          
          {/* Game Content Area */}
          <Box flex="1" overflow="auto" mb={4}>
            <VStack spacing={4} align="stretch">
              {chatMessages.map((message, index) => (
                <ChatMessage key={index} {...message} />
              ))}
            </VStack>
          </Box>

          {showDashboardDicePanel ? (
            <Box
              mb={4}
              p={3}
              borderRadius="md"
              bg="rgba(0, 0, 0, 0.35)"
              border="1px solid rgba(255, 255, 255, 0.08)"
            >
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="semibold" color={mythboundTheme.colors.primary.gold} fontSize="sm">
                  Dice Roller
                </Text>
                <IconButton
                  aria-label="Hide dice roller"
                  size="sm"
                  icon={<MinusIcon />}
                  onClick={() => setShowDashboardDicePanel(false)}
                />
              </Flex>
              <DiceRoller
                sessionId={sessionId}
                playerScreenName={playerScreenName}
                characterName={characterName}
              />
            </Box>
          ) : (
            <Button
              size="sm"
              mb={4}
              leftIcon={<SmallAddIcon />}
              onClick={() => setShowDashboardDicePanel(true)}
              variant="outline"
              colorScheme="teal"
            >
              Show Dice Roller
            </Button>
          )}
          
          {/* Input Area */}
          <Box
            p={3}
            borderRadius="md"
            bg="rgba(59, 232, 255, 0.05)"
            borderWidth="1px"
            borderColor={mythboundTheme.colors.primary.main}
            display="flex"
            alignItems="center"
          >
            <Box
              ref={chatInputRef}
              contentEditable={true}
              flex="1"
              minH="40px"
              px={2}
              py={1}
              borderRadius="md"
              bg="rgba(17, 17, 22, 0.6)"
              color="white"
              _focus={{ outline: 'none', boxShadow: 'outline' }}
              _placeholder={{ color: 'gray.400' }}
              data-placeholder="Type your message here..."
              onKeyDown={handleChatKeyDown}
              suppressContentEditableWarning
            />
            <Button
              ml={3}
              variant="primary"
              onClick={handleSendMessage}
              isLoading={isSending}
            >
              Send
            </Button>
          </Box>
        </Box>
        {showEncounterDicePanel ? (
        <Box
          position="absolute"
          top={{ base: 2, md: 6 }}
          right={{ base: 2, md: 8 }}
          zIndex={10}
          bg="rgba(8, 8, 12, 0.85)"
          border="1px solid rgba(59, 232, 255, 0.4)"
          borderRadius="lg"
          p={3}
          w={{ base: '220px', md: '260px' }}
          boxShadow="0 0 12px rgba(59, 232, 255, 0.4)"
        >
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="sm" color={mythboundTheme.colors.primary.main}>
              Encounter Dice Panel
            </Text>
            <IconButton
              aria-label="Hide encounter dice panel"
              size="xs"
              icon={<MinusIcon />}
              onClick={() => setShowEncounterDicePanel(false)}
            />
          </Flex>
          <DiceRoller
            sessionId={sessionId}
            playerScreenName={playerScreenName}
            characterName={characterName}
            compact
            showHistory={false}
            disabled={false}
          />
        </Box>
        ) : (
        <IconButton
          position="absolute"
          top={{ base: 2, md: 6 }}
          right={{ base: 2, md: 8 }}
          aria-label="Show encounter dice panel"
          size="sm"
          icon={<SmallAddIcon />}
          onClick={() => setShowEncounterDicePanel(true)}
          zIndex={9}
        />
        )}
      </GridItem>
      
      {/* Character Panel - Desktop */}
      {/* Updated 2026-02-23: Uses API-fetched characters, no mock data */}
      <GridItem rowSpan={2} colSpan={1} display={{ base: "none", lg: "block" }}>
        <Box
          h="100%"
          bg="rgba(17, 17, 22, 0.7)"
          borderRadius="md"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.brown}
          p={4}
          overflow="auto"
        >
          <Heading
            size="md"
            mb={4}
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            color={mythboundTheme.colors.primary.gold}
          >
            Characters
          </Heading>

          {charactersLoading ? (
            /* Loading skeleton for character roster */
            <VStack spacing={3} align="stretch">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height="70px" borderRadius="md" />
              ))}
            </VStack>
          ) : charactersError ? (
            <VStack spacing={3} align="stretch" py={4}>
              <Text color={mythboundTheme.colors.functional.error} fontSize="sm" textAlign="center">{charactersError}</Text>
              <Button size="sm" onClick={() => setRosterRetry(value => value + 1)}>Retry roster</Button>
            </VStack>
          ) : characters.length === 0 ? (
            <Text color={mythboundTheme.colors.accent.silver} fontSize="sm" textAlign="center" py={4}>
              No characters in this session yet.
            </Text>
          ) : (
            <VStack spacing={3} align="stretch">
              {characters.map((character, index) => (
                <SessionCharacterCard
                  key={character.character_id || index}
                  name={character.name}
                  race={character.race}
                  classText={character.classText}
                  levelText={character.levelText}
                  avatar={character.avatar}
                  hpText={character.hpText}
                  maxHpText={character.maxHpText}
                  healthRatio={character.healthRatio}
                  isActive={index === activeCharacter}
                  isSpeaking={speakingParticipants.has(character.character_id)}
                  onClick={() => setActiveCharacter(index)}
                />
              ))}
            </VStack>
          )}

          <Divider my={4} borderColor={mythboundTheme.colors.accent.brown} />

          <Heading
            size="sm"
            mb={3}
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            color={mythboundTheme.colors.primary.gold}
          >
            Active Character
          </Heading>

          {characters[activeCharacter] ? (
            <Box
              p={3}
              borderRadius="md"
              bg="rgba(232, 217, 176, 0.05)"
              borderWidth="1px"
              borderColor={mythboundTheme.colors.secondary.parchment}
            >
              <Text fontWeight="bold" mb={2}>
                {characters[activeCharacter].name}
              </Text>
              <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>
                {characters[activeCharacter].race} {characters[activeCharacter].classText}, Level {characters[activeCharacter].levelText}
              </Text>
              <Text fontSize="sm" mt={1}>
                HP: {characters[activeCharacter].hpText}/{characters[activeCharacter].maxHpText}
              </Text>
            </Box>
          ) : (
            <Text color={mythboundTheme.colors.accent.silver} fontSize="sm">
              No character selected
            </Text>
          )}

          <Divider my={4} borderColor={mythboundTheme.colors.accent.brown} />
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Heading 
                size="sm"
                fontFamily={mythboundTheme.typography.fontFamily.heading}
                color={mythboundTheme.colors.primary.gold}
              >
                Sidebar Dice Panel
              </Heading>
              {showSidebarDicePanel ? (
                <IconButton
                  aria-label="Hide sidebar dice panel"
                  size="sm"
                  icon={<MinusIcon />}
                  onClick={() => setShowSidebarDicePanel(false)}
                />
              ) : null}
            </Flex>
            {showSidebarDicePanel ? (
              <DiceRoller
                sessionId={sessionId}
                playerScreenName={playerScreenName}
                characterName={characterName}
                compact
                showHistory={false}
                className="sidebar-dice-panel"
              />
            ) : (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<SmallAddIcon />}
                onClick={() => setShowSidebarDicePanel(true)}
              >
                Show Sidebar Dice
              </Button>
            )}
          </Box>
        </Box>
      </GridItem>
      
      {/* ===================================================================
          GAME CONTROLS BAR
          Issue #021 Fix: All buttons now wired with click handlers
      =================================================================== */}
      <GridItem rowSpan={1} colSpan={{ base: 1, lg: 1 }}>
        <Flex
          bg="rgba(17, 17, 22, 0.7)"
          borderRadius="md"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.brown}
          p={3}
          justify="space-between"
        >
          {/* Left side controls - gameplay actions */}
          <HStack spacing={4}>
            {/* ===============================================================
                Roll Dice: Opens DiceRollerModal
                Provides focused dice rolling interface
            =============================================================== */}
            <GameControl
              icon=""
              label="Roll Dice"
              onClick={handleRollDice}
            />

            {/* Explicit saved campaign notes/history; ordinary chat remains on its own feed. */}
            <GameControl
              icon=""
              label="Saved Notes"
              isActive={isCampaignHistoryOpen}
              onClick={handleNotes}
            />

            {/* ===============================================================
                Map: Opens "Coming Soon" modal

                INTEGRATION POINT: When Map feature is implemented,
                replace with actual map modal/navigation.
            =============================================================== */}
            <GameControl
              icon=""
              label="Map"
              onClick={handleMap}
            />

            {/* ===============================================================
                Spellbook: Opens SpellbookDrawer
                Shows character spells for quick reference
            =============================================================== */}
            <GameControl
              icon=""
              label="Spellbook"
              onClick={handleSpellbook}
            />
          </HStack>

          {/* Right side controls - session management */}
          <HStack spacing={4}>
            {/* Characters: Opens character drawer (ALREADY WORKED) */}
            <GameControl icon="" label="Characters" onClick={onOpen} />

            {/* Voice: Toggles voice capture (ALREADY WORKED) */}
            <GameControl
              icon=""
              label="Voice"
              isActive={isCapturing}
              onClick={handleVoiceToggle}
            />

            {/* ===============================================================
                Settings: Navigates to /settings (404 until implemented)

                INTEGRATION POINT: When Settings feature is ready,
                this will automatically work via the route.
            =============================================================== */}
            <GameControl
              icon=""
              label="Settings"
              onClick={handleSettings}
            />

            {/* Forum: navigates to session discussion forum — added 2026-03-11 */}
            <GameControl
              icon="💬"
              label="Forum"
              onClick={handleForum}
            />
          </HStack>
        </Flex>
      </GridItem>
      
      {/* Mobile Character Drawer -- Updated 2026-02-23: uses API data */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={mythboundTheme.colors.primary.dark}>
          <DrawerCloseButton color={mythboundTheme.colors.secondary.white} />
          <DrawerHeader
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            color={mythboundTheme.colors.primary.gold}
            borderBottomWidth="1px"
            borderColor={mythboundTheme.colors.accent.brown}
          >
            Characters
          </DrawerHeader>
          <DrawerBody>
            {charactersLoading ? (
              <Center py={8}><Spinner color={mythboundTheme.colors.primary.main} /></Center>
            ) : charactersError ? (
              <VStack spacing={3} align="stretch" py={4}>
                <Text color={mythboundTheme.colors.functional.error} fontSize="sm" textAlign="center">{charactersError}</Text>
                <Button size="sm" onClick={() => setRosterRetry(value => value + 1)}>Retry roster</Button>
              </VStack>
            ) : characters.length === 0 ? (
              <Text color={mythboundTheme.colors.accent.silver} fontSize="sm" textAlign="center" py={4}>
                No characters in this session yet.
              </Text>
            ) : (
              <VStack spacing={3} align="stretch" mt={2}>
                {characters.map((character, index) => (
                  <SessionCharacterCard
                    key={character.character_id || index}
                    name={character.name}
                    race={character.race}
                    classText={character.classText}
                    levelText={character.levelText}
                    avatar={character.avatar}
                    hpText={character.hpText}
                    maxHpText={character.maxHpText}
                    healthRatio={character.healthRatio}
                    isActive={index === activeCharacter}
                    isSpeaking={speakingParticipants.has(character.character_id)}
                    onClick={() => {
                      setActiveCharacter(index);
                      onClose();
                    }}
                  />
                ))}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* ===================================================================
          MODALS AND DRAWERS
          Issue #021 Fix: Added for wired game control buttons
      =================================================================== */}

      {/* ===============================================================
          Dice Roller Modal
          Opens when user clicks "Roll Dice" game control
      =============================================================== */}
      <DiceRollerModal
        isOpen={isDiceModalOpen}
        onClose={onDiceModalClose}
        sessionId={sessionId}
        playerScreenName={playerScreenName}
        characterName={characterName}
      />

      {/* ===============================================================
          Spellbook Drawer
          Opens when user clicks "Spellbook" game control

          INTEGRATION POINT: When CharacterService is complete,
          pass the actual character ID from session context.
      =============================================================== */}
      <SpellbookDrawer
        isOpen={isSpellbookOpen}
        onClose={onSpellbookClose}
        characterId={characterId}
        characterName={characters[activeCharacter]?.name || characterName}
      />

      {/* ===============================================================
          Map Coming Soon Modal
          Shows when user clicks "Map" game control

          INTEGRATION POINT: When Map feature is implemented:
          1. Create MapModal or GameMap component
          2. Replace this with the actual map component
          3. Remove ComingSoonModal usage
      =============================================================== */}
      <ComingSoonModal
        isOpen={isMapModalOpen}
        onClose={onMapModalClose}
        featureName="Game Map"
        description="Interactive battle maps with fog of war, token movement, and real-time synchronization are being crafted by our artificers."
      />

      {/* ===================================================================
          VOICE PANEL — Floating overlay (bottom-right)
          Updated 2026-02-23: Section 5 Voice Bridge UI Wiring
          Composes VoiceControls, VoiceStatus, and VoiceBridgeInfo into a
          single minimizable panel. Shows participant count + connection dot
          when minimized, full controls when expanded.
      =================================================================== */}
      {sessionId && participantId && (
        <VoicePanel
          sessionId={sessionId}
          participantId={participantId}
          isGM={isGM}
          sessionStatus={sessionState?.session_id === sessionId ? sessionState.status : null}
          masterModeEnabled={sessionState?.session_id === sessionId ? Boolean(sessionState.master_mode_enabled) : false}
          onMasterModeChange={(enabled) => {
            if (!sessionState || sessionState.session_id !== sessionId) {
              return Promise.reject(new Error('Session state is not available yet.'));
            }
            return setMasterMode(sessionState.session_id, enabled);
          }}
        />
      )}
    </Grid>
    {/* PRD004.8: notes/history are a separate explicit persistence surface. The session
        identity/status comes only from matching SessionContext state, the preview only from the
        same authorized campaign snapshot, and HTTP writes revalidate membership server-side. */}
    <CampaignHistory
      campaignId={historyCampaignId}
      sessionId={sessionState?.session_id === sessionId ? sessionId : null}
      viewerId={userInfo?.id ?? null}
      sessionStatus={sessionState?.session_id === sessionId && !campaignRealtime?.pinnedSessionChanged ? sessionState.status : null}
      snapshotEntries={historySnapshot?.history ?? []}
      onRefreshSnapshot={campaignRealtime?.refreshSnapshot}
      isOpen={isCampaignHistoryOpen}
    />
    </>
  );
};

export default SessionView;
